import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react'
import { modalClasses } from '@mui/material'

const KEY_OWNING_ROLES = ['dialog', 'menu', 'menubar', 'listbox', 'combobox', 'tree', 'grid', 'treegrid', 'tablist', 'radiogroup', 'spinbutton', 'slider']

/**
 * Elements that use the keys themselves. A modal's own root counts too: our dialogs disable their Escape,
 * and the focus can rest on a dialog's or popover's container, outside its `role="dialog"`
 */
const KEY_OWNING_SELECTOR = [
    'input',
    'textarea',
    'select',
    '[contenteditable]:not([contenteditable="false"])',
    ...KEY_OWNING_ROLES.map((role) => `[role="${role}"]`),
    `.${modalClasses.root}`,
].join(', ')

/** Shift+Enter has a meaning of its own on these, e.g. a new window for a link */
const INTERACTIVE_SELECTOR = 'a[href], button, [role="button"]'

type ShortcutHandlers = Readonly<{
    onPrevious: VoidFunction | undefined
    onNext: VoidFunction | undefined
    onExpand: VoidFunction | undefined
    onClose: VoidFunction
}>

const closest = (target: EventTarget | null, selector: string): Element | null => (target instanceof Element ? target.closest(selector) : null)

const findShortcutHandler = (
    event: KeyboardEvent,
    { onPrevious, onNext, onExpand, onClose }: ShortcutHandlers,
    header: HTMLElement | null
): VoidFunction | undefined => {
    if (event.defaultPrevented || event.repeat || event.altKey || event.ctrlKey || event.metaKey || closest(event.target, KEY_OWNING_SELECTOR)) {
        return undefined
    }

    if (event.shiftKey) {
        const control = closest(event.target, INTERACTIVE_SELECTOR)
        const isOtherControl = control !== null && !header?.contains(control)
        return event.key === 'Enter' && !isOtherControl ? onExpand : undefined
    }

    switch (event.key) {
        case 'ArrowUp':
            return onPrevious
        case 'ArrowDown':
            return onNext
        case 'Escape':
            return onClose
        default:
            return undefined
    }
}

/** While enabled, a key whose button is shown and enabled acts as a click on it; Shift+Enter also from the header's own buttons */
export const useRecordPanelShortcuts = (enabled: boolean, handlers: ShortcutHandlers, header: RefObject<HTMLElement | null>): void => {
    const handlersRef = useRef(handlers)

    useLayoutEffect(() => {
        handlersRef.current = handlers
    })

    useEffect(() => {
        if (!enabled) {
            return
        }

        const onKeyDown = (event: KeyboardEvent): void => {
            const handler = findShortcutHandler(event, handlersRef.current, header.current)

            if (handler) {
                event.preventDefault()
                handler()
            }
        }

        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [enabled, header])
}
